import { Helmet } from "react-helmet-async";

interface PageMetaProps {
  title: string;
  description: string;
  path: string;
  image?: string;
  type?: "website" | "article";
  /** Keep this page out of search results (app/utility pages, 404s). */
  noindex?: boolean;
}

const SITE_URL = "https://pleasantroutes.com";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

/**
 * Per-route SEO meta tags. Sets a unique <title>, description,
 * canonical link, and Open Graph / Twitter tags for the current page.
 *
 * The static index.html keeps homepage og:* tags as a fallback for
 * non-JS social-preview crawlers; this component overrides them for
 * JS-executing crawlers (Googlebot etc.).
 */
export function PageMeta({
  title,
  description,
  path,
  image,
  type = "website",
  noindex = false,
}: PageMetaProps) {
  const url = `${SITE_URL}${path}`;
  const ogImage = image ?? DEFAULT_IMAGE;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, follow" />}
      {!noindex && <link rel="canonical" href={url} />}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
